import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getCourse, getEnrollments } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, BookOpen, ChevronDown, ChevronRight, Folder, FolderOpen,
  FileText, CheckCircle2, Play, ClipboardList, ArrowLeft, Home,
  Sparkles, Clock, GraduationCap, PlayCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHdr = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const toEmbedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  if (url.match(/\.(mp4|webm|ogg)(\?|$)/i)) return url;
  return null;
};

const buildTree = (modules) => {
  const map = {};
  modules.forEach(m => { map[m.module_id] = { ...m, children: [] }; });
  const roots = [];
  modules.forEach(m => {
    if (m.parent_id && map[m.parent_id]) {
      map[m.parent_id].children.push(map[m.module_id]);
    } else {
      roots.push(map[m.module_id]);
    }
  });
  const sortRec = (nodes) => {
    nodes.sort((a, b) => (a.order || 0) - (b.order || 0));
    nodes.forEach(n => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

export default function CoursePlayer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [markingDone, setMarkingDone] = useState(false);

  useEffect(() => { loadCourseData(); /* eslint-disable-next-line */ }, [courseId]);

  const loadCourseData = async () => {
    try {
      const [courseRes, enrRes, asgRes] = await Promise.all([
        getCourse(courseId),
        getEnrollments(),
        axios.get(`${API}/content/courses/${courseId}/assignments`, { headers: authHdr() }).catch(() => ({ data: [] })),
      ]);
      const c = courseRes.data;
      setCourse(c);
      setAssignments(asgRes.data || []);
      const userEnroll = (enrRes.data || []).find(e => e.course_id === courseId);
      setEnrollment(userEnroll);
      // Auto-pick first topic — respecting the student's year filter
      const mods = c?.modules || [];
      const userYear = user?.current_year;
      const byId = {};
      mods.forEach(m => { byId[m.module_id] = m; });
      // Find the Year-N root (if year-filtered) — else all modules are eligible
      let eligibleSet = null;
      if (userYear) {
        const yearRoot = mods.find(m => m.parent_id === null && (m.title || '').trim().toLowerCase() === `year ${userYear}`.toLowerCase());
        if (yearRoot) {
          eligibleSet = new Set();
          const walk = (id) => {
            eligibleSet.add(id);
            mods.filter(m => m.parent_id === id).forEach(ch => walk(ch.module_id));
          };
          walk(yearRoot.module_id);
        }
      }
      const isEligible = (m) => !eligibleSet || eligibleSet.has(m.module_id);

      const firstTopic = mods.find(m => isEligible(m) && m.type === 'topic' && (m.video_url || m.content_markdown))
                     || mods.find(m => isEligible(m) && m.type === 'topic');
      if (firstTopic) {
        setActiveTopicId(firstTopic.module_id);
        // Auto-expand ancestors
        const exp = {};
        let cur = byId[firstTopic.parent_id];
        while (cur) { exp[cur.module_id] = true; cur = byId[cur.parent_id]; }
        setExpanded(exp);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load course');
    }
    setLoading(false);
  };

  const markDone = async () => {
    if (!enrollment || !activeTopicId) return;
    setMarkingDone(true);
    try {
      const res = await axios.post(
        `${API}/enrollments/${enrollment.enrollment_id}/progress?video_id=${activeTopicId}`,
        {},
        { headers: authHdr() }
      );
      setEnrollment(e => ({ ...e, progress: res.data.progress, completed_videos: res.data.completed_videos }));
      toast.success('Marked as completed!');
    } catch (err) {
      toast.error('Failed to mark progress');
    }
    setMarkingDone(false);
  };

  const topics = useMemo(() =>
    (course?.modules || []).filter(m => m.type === 'topic'), [course]);

  const activeTopic = useMemo(() =>
    activeTopicId ? (course?.modules || []).find(m => m.module_id === activeTopicId) : null,
    [activeTopicId, course]);

  const topicAssignments = useMemo(() =>
    activeTopic ? assignments.filter(a => a.module_id === activeTopic.module_id) : [],
    [activeTopic, assignments]);

  const breadcrumb = useMemo(() => {
    if (!activeTopic || !course) return [];
    const byId = {};
    (course.modules || []).forEach(m => { byId[m.module_id] = m; });
    const path = [];
    let cur = activeTopic;
    while (cur) { path.unshift(cur.title); cur = cur.parent_id ? byId[cur.parent_id] : null; }
    return path;
  }, [activeTopic, course]);

  const tree = useMemo(() => {
    const fullTree = buildTree(course?.modules || []);
    // Filter by user's current year (if set).
    // Students only see the top-level "Year N" folder matching their year.
    // If no year is configured → show whole tree (backward compat for older courses
    // that don't use the Year 1/2/3/4 top-level structure).
    const userYear = user?.current_year;
    if (!userYear) return fullTree;
    const yearLabel = `Year ${userYear}`;
    const matched = fullTree.filter(n => (n.title || '').trim().toLowerCase() === yearLabel.toLowerCase());
    return matched.length > 0 ? matched : fullTree;
  }, [course, user]);

  // Topics visible to this student (respecting year filter)
  const visibleTopics = useMemo(() => {
    const collect = (nodes, acc) => {
      nodes.forEach(n => {
        const isFolder = n.type === 'folder' || (n.children && n.children.length > 0);
        if (isFolder) collect(n.children || [], acc);
        else acc.push(n);
      });
      return acc;
    };
    return collect(tree, []);
  }, [tree]);

  const isCompleted = (topicId) => (enrollment?.completed_videos || []).includes(topicId);
  const totalTopics = visibleTopics.length;
  const completedCount = (enrollment?.completed_videos || []).filter(id => visibleTopics.some(t => t.module_id === id)).length;
  const progressPct = totalTopics ? Math.round((completedCount / totalTopics) * 100) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-3" />
          <div className="text-lg font-medium">Course not found</div>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* ==================== SIDEBAR ==================== */}
      <aside className="w-80 border-r border-border bg-card/30 flex flex-col h-screen sticky top-0">
        {/* Header */}
        <div className="p-4 border-b border-border shrink-0">
          <button onClick={() => navigate('/dashboard')}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition mb-3"
            data-testid="player-back">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </button>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0 shadow-md shadow-emerald-500/30">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-sm leading-tight line-clamp-2">{course.title}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">{course.category || 'Course'}</div>
            </div>
          </div>
          {enrollment && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1">
                <span>Progress</span>
                <span className="font-medium text-emerald-400">{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
              <div className="text-[10px] text-muted-foreground mt-1">
                {completedCount} / {totalTopics} topics complete
              </div>
            </div>
          )}
        </div>

        {/* Tree */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {tree.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Curriculum is being prepared.<br />Check back soon!
            </div>
          ) : (
            tree.map(node => (
              <TreeNodeView
                key={node.module_id}
                node={node}
                depth={0}
                expanded={expanded}
                setExpanded={setExpanded}
                activeId={activeTopicId}
                onSelect={setActiveTopicId}
                isCompleted={isCompleted}
              />
            ))
          )}
        </nav>

        {/* AI Tutor quick-link */}
        <div className="p-3 border-t border-border shrink-0">
          <Button
            onClick={() => navigate('/dashboard/ai-tutor')}
            variant="outline"
            size="sm"
            className="w-full justify-start bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border-violet-500/30 hover:from-violet-500/20 hover:to-fuchsia-500/20"
            data-testid="player-ai-tutor"
          >
            <Sparkles className="w-4 h-4 mr-2 text-violet-400" /> Ask AI Tutor
          </Button>
        </div>
      </aside>

      {/* ==================== MAIN PANE ==================== */}
      <main className="flex-1 min-w-0">
        {activeTopic ? (
          <TopicView
            topic={activeTopic}
            breadcrumb={breadcrumb}
            courseTitle={course.title}
            assignments={topicAssignments}
            completed={isCompleted(activeTopic.module_id)}
            markDone={markDone}
            markingDone={markingDone}
            enrollment={enrollment}
          />
        ) : (
          <div className="max-w-3xl mx-auto px-8 py-20 text-center">
            <BookOpen className="w-20 h-20 text-muted-foreground/30 mx-auto mb-4" />
            <div className="text-xl font-semibold">Welcome to {course.title}</div>
            <div className="text-sm text-muted-foreground mt-2">
              Pick any topic from the sidebar to start learning.
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ==================== TREE NODE (recursive) ====================
function TreeNodeView({ node, depth, expanded, setExpanded, activeId, onSelect, isCompleted }) {
  const isFolder = node.type === 'folder' || (node.children && node.children.length > 0);
  const isOpen = !!expanded[node.module_id];
  const isActive = activeId === node.module_id;
  const done = !isFolder && isCompleted(node.module_id);

  const handleClick = () => {
    if (isFolder) setExpanded(p => ({ ...p, [node.module_id]: !isOpen }));
    else onSelect(node.module_id);
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full flex items-center gap-1.5 py-1 pr-2 rounded-md text-[13px] text-left transition-colors ${
          isActive
            ? 'bg-emerald-500/15 text-emerald-400 font-medium'
            : isFolder
              ? 'text-foreground/85 hover:bg-accent'
              : done
                ? 'text-emerald-400/80 hover:bg-accent'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
        }`}
        style={{ paddingLeft: `${depth * 14 + 6}px` }}
        data-testid={`student-tree-${node.module_id}`}
      >
        {isFolder ? (
          isOpen
            ? <ChevronDown className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
            : <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        ) : <span className="w-3.5 shrink-0" />}
        {isFolder
          ? (isOpen ? <FolderOpen className="w-3.5 h-3.5 shrink-0 text-amber-400/80" /> : <Folder className="w-3.5 h-3.5 shrink-0 text-amber-400/70" />)
          : done
            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-emerald-500" />
            : <FileText className="w-3.5 h-3.5 shrink-0 text-sky-400/70" />
        }
        <span className="flex-1 truncate">{node.title}</span>
        {!isFolder && node.video_url && <PlayCircle className="w-3 h-3 shrink-0 text-sky-400/60" />}
      </button>
      {isFolder && isOpen && (node.children || []).map(child => (
        <TreeNodeView
          key={child.module_id}
          node={child}
          depth={depth + 1}
          expanded={expanded}
          setExpanded={setExpanded}
          activeId={activeId}
          onSelect={onSelect}
          isCompleted={isCompleted}
        />
      ))}
    </div>
  );
}

// ==================== TOPIC VIEW (right pane) ====================
function TopicView({ topic, breadcrumb, courseTitle, assignments, completed, markDone, markingDone, enrollment }) {
  const embed = toEmbedUrl(topic.video_url);
  return (
    <div className="max-w-4xl mx-auto px-8 py-8" data-testid="topic-view">
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
        <div className="min-w-0 flex-1">
          <div className="text-xs text-muted-foreground mb-1 truncate">
            {courseTitle} · {breadcrumb.slice(0, -1).join(' › ') || 'Topic'}
          </div>
          <h1 className="text-2xl font-bold">{topic.title}</h1>
          {topic.description && (
            <p className="text-sm text-muted-foreground mt-1">{topic.description}</p>
          )}
        </div>
        {enrollment && (
          <Button
            onClick={markDone}
            disabled={completed || markingDone}
            className={completed
              ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30'
              : 'bg-gradient-to-r from-emerald-500 to-teal-600'
            }
            data-testid="mark-complete"
          >
            {markingDone ? <Loader2 className="w-4 h-4 animate-spin" />
              : completed ? <><CheckCircle2 className="w-4 h-4 mr-2" /> Completed</>
              : <><CheckCircle2 className="w-4 h-4 mr-2" /> Mark Complete</>}
          </Button>
        )}
      </div>

      {/* ========= VIDEO ========= */}
      {embed ? (
        <div className="mb-8">
          <div className="rounded-xl overflow-hidden border border-border bg-black aspect-video shadow-xl">
            {embed.match(/\.(mp4|webm|ogg)(\?|$)/i) ? (
              <video src={embed} controls className="w-full h-full" />
            ) : (
              <iframe src={embed} className="w-full h-full" allowFullScreen title={topic.title} />
            )}
          </div>
          {topic.duration_minutes > 0 && (
            <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {topic.duration_minutes} min
            </div>
          )}
        </div>
      ) : (
        <Card className="mb-8 bg-muted/30 border-dashed">
          <CardContent className="p-8 text-center">
            <PlayCircle className="w-12 h-12 text-muted-foreground/40 mx-auto mb-2" />
            <div className="text-sm text-muted-foreground">Video coming soon for this topic.</div>
          </CardContent>
        </Card>
      )}

      {/* ========= READING MATERIAL ========= */}
      {topic.content_markdown && (
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
              <FileText className="w-4 h-4 text-emerald-500" />
              <h3 className="font-semibold text-sm">Reading Material</h3>
            </div>
            <div className="prose prose-invert prose-sm max-w-none prose-img:rounded-lg prose-a:text-emerald-400 prose-headings:text-foreground prose-strong:text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{topic.content_markdown}</ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========= ASSIGNMENTS ========= */}
      {assignments.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
              <ClipboardList className="w-4 h-4 text-violet-400" />
              <h3 className="font-semibold text-sm">Assignments</h3>
              <Badge variant="outline" className="text-[10px] ml-auto">{assignments.length}</Badge>
            </div>
            <div className="space-y-3">
              {assignments.map(a => (
                <div key={a.assignment_id} className="p-4 rounded-lg border border-border hover:border-violet-500/30 transition" data-testid={`student-assign-${a.assignment_id}`}>
                  <div className="font-medium text-sm">{a.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{a.description}</div>
                  {a.instructions && (
                    <div className="mt-3 p-3 rounded bg-muted/40 text-xs whitespace-pre-wrap">
                      <span className="text-muted-foreground uppercase text-[10px] tracking-wider block mb-1">Instructions</span>
                      {a.instructions}
                    </div>
                  )}
                  <div className="flex gap-2 mt-3 text-[11px] text-muted-foreground">
                    <span>{a.max_points || 100} points</span>
                    {a.due_date && <span>· Due {a.due_date}</span>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!embed && !topic.content_markdown && assignments.length === 0 && (
        <div className="text-center py-16 text-sm text-muted-foreground">
          Your instructor is preparing this topic. Check back soon! 📚
        </div>
      )}

      {/* Prev / Next hint */}
      <div className="mt-12 pt-6 border-t border-border text-xs text-muted-foreground text-center">
        <Home className="w-3 h-3 inline mr-1" /> Use the curriculum tree on the left to navigate topics
      </div>
    </div>
  );
}
